/**
 * GA4 promotion tracking for co-op banners/image tiles.
 * Spec: site-banner-promotion-tracking-spec-2026-10-13.md (Dave -> Dustin, 2026-10-13)
 *
 * Watches every element carrying data-promotion-slot/-name/-creative(/-items), PLUS
 * every Quicky app-embed banner (id="qb-<24-hex-objectid>", third-party banner app -
 * see collection pages like /collections/trimmers-harvest), and pushes standard GA4
 * ecommerce events to window.dataLayer:
 *   - view_promotion: once per promotion_id per page view, when >=50% of the element is visible
 *   - select_promotion: on click, before navigation
 *
 * Quicky banners target brands/collections, not hosting pages - one banner can target
 * many collection pages, and several different brands' banners commonly stack on one
 * page (confirmed live: EZTrim/Fox Farm/ClimateIQ banners all on one trimmers-harvest
 * page load). So creative_slot for a Quicky banner is keyed off the banner's OWN target
 * collection/product handle (already present in its data-settings JSON), not the page
 * it happens to render on - that keeps one continuous slot/history across creative
 * refreshes for the same co-op partner, and never collides with a different brand's
 * banner sharing the same hosting page. Which page(s) it showed on is still available
 * via GA4's own default page_location parameter, no need to bake that into the slot.
 *
 * GTM is responsible for a trigger + GA4 event tag that reads these two event names
 * off the dataLayer - that wiring is out of scope for this file.
 */
(function () {
  if (window.__promotionTrackingInit) return;
  window.__promotionTrackingInit = true;

  var seenViewIds = Object.create(null);
  var slideClassObserver = null;

  function slugify(value) {
    return (value || '')
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-+|-+$)/g, '');
  }

  function currentYearMonth() {
    var now = new Date();
    var month = now.getMonth() + 1;
    return '' + now.getFullYear() + (month < 10 ? '0' + month : month);
  }

  var QUICKY_ID_RE = /^qb-[0-9a-f]{24}$/i;

  // Strips the HTML Quicky stores in button/message text (e.g. "<p><strong>SHOP
  // FOXFARM</strong></p>") down to plain text for creative_name.
  function stripHtml(value) {
    return (value || '').toString().replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  }

  // One Quicky banner's settings -> {name, slot, creative} or null if untargeted.
  function quickyPromotionFromSettings(settings) {
    if (!settings) return null;
    var target = settings.collection || settings.product;
    if (!target || !target.title || !target.handle) return null;
    var creative = stripHtml(settings.button && settings.button.text) || stripHtml(settings.message && settings.message.text);
    return {
      promotion_name: target.title,
      creative_slot: 'collection-banner-' + target.handle,
      creative_name: creative
    };
  }

  // A Quicky block's data-settings is either one banner directly (action bar) or
  // { banners: [...] } (multi-slide bar). Each multi-slide entry is wrapped as
  // { barId, settings } (confirmed live 2026-10-26 on /collections/trimmers-harvest),
  // so the banner's own collection/product target lives one level down. Slides are
  // rendered in banners[] order (randomOrderMode is false on every live bar), so the
  // currently shown slide maps back to its banner by index.
  function quickyBanners(el) {
    var raw = el.getAttribute('data-settings');
    if (!raw) return [];
    var settings;
    try {
      settings = JSON.parse(raw);
    } catch (e) {
      return [];
    }
    if (settings && Array.isArray(settings.banners)) {
      return settings.banners.map(function (b) { return (b && b.settings) ? b.settings : b; });
    }
    return settings ? [settings] : [];
  }

  function quickyCurrentBannerIndex(el, count) {
    if (count < 2) return 0;
    var slides = el.querySelectorAll('li.slide');
    if (!slides.length) return 0;
    var real = [];
    for (var i = 0; i < slides.length; i++) {
      if (!/\bclone\b/.test(slides[i].className)) real.push(slides[i]);
    }
    if (!real.length) real = Array.prototype.slice.call(slides);
    for (var j = 0; j < real.length; j++) {
      if (/\bselected\b/.test(real[j].className)) return j % count;
    }
    return 0;
  }

  function quickyPromotionForElement(el) {
    var banners = quickyBanners(el);
    if (!banners.length) return null;
    var idx = quickyCurrentBannerIndex(el, banners.length);
    return quickyPromotionFromSettings(banners[idx]) || quickyPromotionFromSettings(banners[0]);
  }

  function buildPromotion(el) {
    var slot, name, creativeName, itemsAttr;

    if (QUICKY_ID_RE.test(el.id || '')) {
      var quicky = quickyPromotionForElement(el);
      if (!quicky) return null;
      slot = quicky.creative_slot;
      name = quicky.promotion_name;
      creativeName = quicky.creative_name;
    } else {
      slot = el.getAttribute('data-promotion-slot');
      name = el.getAttribute('data-promotion-name');
      if (!slot || !name) return null;
      creativeName = el.getAttribute('data-promotion-creative') || '';
      itemsAttr = el.getAttribute('data-promotion-items');
    }

    var promotion = {
      promotion_name: name,
      promotion_id: slugify(name) + '-' + slot + '-' + currentYearMonth(),
      creative_slot: slot,
      creative_name: creativeName || ''
    };

    if (itemsAttr) {
      var items = itemsAttr
        .split(',')
        .map(function (sku) { return sku.trim(); })
        .filter(function (sku) { return sku.length > 0; })
        .map(function (sku) { return { item_id: sku }; });
      if (items.length) promotion.items = items;
    }

    return promotion;
  }

  function pushEvent(eventName, el) {
    var promotion = buildPromotion(el);
    if (!promotion) return;

    window.dataLayer = window.dataLayer || [];
    // GTM merges ecommerce.items from whatever ecommerce event fired last
    // (view_item, view_item_list, etc.) into the next one unless the
    // previous ecommerce object is explicitly cleared first - without this,
    // a banner next to a product list would report that list's products
    // instead of its own. Per GA4/GTM's own documented ecommerce pattern.
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: eventName,
      promotion_name: promotion.promotion_name,
      promotion_id: promotion.promotion_id,
      creative_slot: promotion.creative_slot,
      creative_name: promotion.creative_name,
      ecommerce: {
        items: promotion.items || []
      }
    });
  }

  function fireView(el) {
    var promotion = buildPromotion(el);
    if (!promotion || seenViewIds[promotion.promotion_id]) return;
    seenViewIds[promotion.promotion_id] = true;
    pushEvent('view_promotion', el);
  }

  function fireSelect(el) {
    pushEvent('select_promotion', el);
  }

  function isAtLeastHalfVisible(el) {
    var rect = el.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return false;
    var visibleHeight = Math.min(rect.bottom, window.innerHeight || document.documentElement.clientHeight) - Math.max(rect.top, 0);
    return visibleHeight > 0 && visibleHeight / rect.height >= 0.5;
  }

  var visibilityObserver = ('IntersectionObserver' in window)
    ? new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var el = entry.target;
          // Carousel slides: only count the slide actually being shown right now.
          if (el.classList.contains('slideshow__slide') && !el.classList.contains('is-selected')) return;
          // Quicky keeps every campaign's config block in the DOM (hidden, zero-size) and
          // only moves the one it shows into its slot; a zero-size target can still report
          // isIntersecting, so require real on-screen size before counting a view.
          if (!isAtLeastHalfVisible(el)) return;
          fireView(el);
        });
      },
      { threshold: 0.5 }
    )
    : null;

  function getSlideClassObserver() {
    if (slideClassObserver) return slideClassObserver;
    slideClassObserver = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        var el = mutation.target;
        if (el.classList.contains('is-selected') && isAtLeastHalfVisible(el)) {
          fireView(el);
        }
      });
    });
    return slideClassObserver;
  }

  function initElement(el) {
    if (el.__promotionTrackingBound) return;
    el.__promotionTrackingBound = true;

    if (visibilityObserver) {
      visibilityObserver.observe(el);
    } else if (isAtLeastHalfVisible(el)) {
      // No IntersectionObserver support: fall back to firing on bind if already visible.
      fireView(el);
    }

    if (el.classList.contains('slideshow__slide')) {
      if ('MutationObserver' in window) {
        getSlideClassObserver().observe(el, { attributes: true, attributeFilter: ['class'] });
      }
      if (el.classList.contains('is-selected') && isAtLeastHalfVisible(el)) {
        fireView(el);
      }
    }

    el.addEventListener('click', function () {
      fireSelect(el);
    });
  }

  function initAll(root) {
    var scope = root || document;
    if (!scope.querySelectorAll) return;
    var elements = scope.querySelectorAll('[data-promotion-slot]');
    for (var i = 0; i < elements.length; i++) {
      initElement(elements[i]);
    }
  }

  // Quicky renders its skeleton markup (with the real id already set) up front, then
  // hydrates data-settings onto it client-side once its own script fetches campaign
  // data - so a qb- element can exist before it's trackable. tryInitQuicky re-checks
  // (on first sight and on every data-settings mutation) and only binds once it has
  // a parseable, targeted promotion.
  function tryInitQuicky(el) {
    if (el.__promotionTrackingBound) return;
    if (quickyPromotionForElement(el)) initElement(el);
  }

  function scanForQuickyBanners(root) {
    var scope = root || document;
    if (!scope.querySelectorAll) return;
    var elements = scope.querySelectorAll('[id^="qb-"]');
    for (var i = 0; i < elements.length; i++) {
      if (QUICKY_ID_RE.test(elements[i].id)) tryInitQuicky(elements[i]);
    }
  }

  if ('MutationObserver' in window) {
    new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.type === 'attributes') {
          if (QUICKY_ID_RE.test(mutation.target.id || '')) tryInitQuicky(mutation.target);
          return;
        }
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          if (QUICKY_ID_RE.test(node.id || '')) tryInitQuicky(node);
          if (node.querySelectorAll) scanForQuickyBanners(node);
        });
      });
    }).observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-settings']
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
      scanForQuickyBanners(document);
    });
  } else {
    initAll(document);
    scanForQuickyBanners(document);
  }

  // Theme editor: re-bind when a section with banners is added/re-rendered.
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
    scanForQuickyBanners(event.target);
  });
})();
